<div class="container-fluid complyon-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell mx-auto">
    <div class="mb-3">
      <h1 class="complyon-title mb-2 text-l">Organisation structure</h1>
      <p class="complyon-subtitle mb-0 text-s">Departments, business units, regions and reporting lines - the scaffolding every risk, control and obligation maps to so dashboards roll up correctly.</p>
    </div>

    <div class="row g-3 mb-3">
      <div v-for="card in summary_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
        <div class="complyon-card rounded-4 p-3 h-100">
          <div class="text-uppercase text-xs text-secondary fw-semibold">{{ card.label }}</div>
          <div class="text-l fw-bold mt-1">{{ card.value }}</div>
          <div class="text-xs text-secondary mt-3">{{ card.note }}</div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <div class="col-12 col-xl-9">
        <div class="complyon-panel rounded-4 overflow-hidden h-100">
          <div class="px-3 px-md-4 pt-3 pt-md-4 pb-3 border-bottom">
            <div class="complyon-section-title text-s">Reporting structure</div>
            <div class="complyon-section-subtitle text-xs">Every risk, control, obligation and task names a node here as accountable owner - open any unit to see what maps to it and where it rolls up.</div>
          </div>

          <div class="px-3 px-md-4 py-3 py-md-4">
            <div class="d-flex flex-column gap-3">
              <div v-for="node in reporting_tree" :key="node.id" class="d-flex flex-column gap-3">
                <div class="d-flex align-items-start gap-2">
                  <button
                    v-if="node.children && node.children.length"
                    type="button"
                    class="btn btn-link text-decoration-none p-0 flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                    style="width: 1rem; height: 1rem; margin-top: .2rem;"
                    @click="toggle_node(node.id)"
                    :aria-expanded="is_open(node.id)"
                    :aria-label="`${is_open(node.id) ? 'Collapse' : 'Expand'} ${node.title}`"
                  >
                    <i class="bi" :class="is_open(node.id) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                  </button>
                  <span v-else class="flex-shrink-0" style="width: 1rem; height: 1rem; margin-top: .2rem;"></span>
                  <div class="d-flex align-items-start gap-3 min-w-0">
                    <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-success-subtle text-success-emphasis" style="width: 2rem; height: 2rem;">
                      <i class="bi" :class="node.icon"></i>
                    </div>
                    <div class="min-w-0">
                      <div class="fw-semibold text-s">{{ node.title }}</div>
                      <div class="text-xs text-secondary">{{ node.meta }}</div>
                    </div>
                  </div>
                </div>

                <div v-show="is_open(node.id)" class="ms-4 ps-3 border-start d-flex flex-column gap-3">
                  <div v-for="child in node.children" :key="child.id" class="d-flex flex-column gap-3">
                    <div class="d-flex align-items-start justify-content-between gap-3">
                      <div class="d-flex align-items-start gap-2 min-w-0">
                        <button
                          v-if="child.children && child.children.length"
                          type="button"
                          class="btn btn-link text-decoration-none p-0 flex-shrink-0 d-inline-flex align-items-center justify-content-center"
                          style="width: 1rem; height: 1rem; margin-top: .2rem;"
                          @click="toggle_node(child.id)"
                          :aria-expanded="is_open(child.id)"
                          :aria-label="`${is_open(child.id) ? 'Collapse' : 'Expand'} ${child.title}`"
                        >
                          <i class="bi" :class="is_open(child.id) ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                        </button>
                        <span v-else class="flex-shrink-0" style="width: 1rem; height: 1rem; margin-top: .2rem;"></span>
                        <div class="d-flex align-items-start gap-3 min-w-0">
                          <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-info-subtle text-info-emphasis" style="width: 1.75rem; height: 1.75rem;">
                            <i class="bi" :class="child.icon"></i>
                          </div>
                          <div class="min-w-0">
                            <div class="fw-semibold">{{ child.title }}</div>
                            <div class="text-xs text-secondary">{{ child.meta }}</div>
                          </div>
                        </div>
                      </div>
                      <span v-if="child.status" class="badge rounded-pill" :class="child.status_class">{{ child.status }}</span>
                      <i v-else-if="!child.children || !child.children.length" class="bi bi-arrow-right text-secondary align-self-center"></i>
                    </div>

                    <div v-if="child.children && child.children.length && is_open(child.id)" class="ms-4 ps-3 border-start d-flex flex-column gap-2">
                      <div v-for="leaf in child.children" :key="leaf.id" class="d-flex align-items-start justify-content-between gap-3">
                        <div class="d-flex align-items-start gap-3 min-w-0">
                          <div class="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-light text-secondary border border-light" style="width: 1.75rem; height: 1.75rem;">
                            <i class="bi" :class="leaf.icon"></i>
                          </div>
                          <div class="min-w-0">
                            <div class="fw-semibold">{{ leaf.title }}</div>
                            <div class="text-xs text-secondary">{{ leaf.meta }}</div>
                          </div>
                        </div>
                        <span v-if="leaf.status" class="badge rounded-pill" :class="leaf.status_class">{{ leaf.status }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-3">
        <div class="complyon-panel rounded-4 overflow-hidden h-100">
          <div class="px-3 px-md-4 pt-3 pt-md-4 pb-3 border-bottom">
            <div class="complyon-section-title text-s">Regions</div>
            <div class="complyon-section-subtitle text-xs">Compliance by geography</div>
          </div>
          <div class="px-3 px-md-4 py-3">
            <div v-for="region in regions" :key="region.label" class="d-flex align-items-start justify-content-between gap-3 py-3 border-bottom">
              <div>
                <div class="fw-semibold">{{ region.label }}</div>
                <div class="text-xs text-secondary">{{ region.meta }}</div>
              </div>
              <span class="badge rounded-pill" :class="region.status_class">{{ region.status }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
